import React, { ReactNode } from 'react';
import slugify from 'slugify';
import styled, { css, ThemeProvider } from 'styled-components';
import { createUrl } from 'playroom';
import { Anchor } from '@carta/ink';
import useWindowWidth from '../../hooks/useWindowWidth';
import { BackstopRemove } from './backstop-helpers';
import convertSampleName from '../helpers/convertSampleName';

const StyledExposeDescribeWrapper = styled.div`
  padding: 12px;
  margin-bottom: 40px;
`;

const StyledSampleInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin: 4px 0 8px;

  small {
    font-size: 12px;
  }
`;

const StyledExposeContent = styled.div`
  padding: 20px;
  border: 1px solid #cfcfcf;

  ${({ theme }) =>
    theme.highlightEnvs &&
    css`
      ${theme.exposeOnly &&
      css`
        box-shadow: 0 0 0 2px aquamarine;
      `}

      ${theme.docs &&
      css`
        box-shadow: 0 0 0 2px plum;
      `}
    `};
`;

const SampleTitle = ({ title }) => {
  const [state, setState] = React.useState<'initial' | 'hovering' | 'copied'>('initial');
  const props = {
    onMouseEnter: () => state !== 'copied' && setState('hovering'),
    onMouseLeave: () => state !== 'copied' && setState('initial'),
    onClick: () => {
      setState('copied');
      navigator.clipboard.writeText(convertSampleName(title));
    },
  };

  React.useEffect(() => {
    if (state !== 'copied') {
      return;
    }

    const timeout = setTimeout(() => setState('initial'), 1000);
    return () => clearTimeout(timeout);
  }, [state]);

  const text = {
    initial: title,
    hovering: convertSampleName(title),
    copied: 'Copied!',
  };

  return (
    <div title="Click to copy to clipboard" style={{ cursor: 'pointer' }} {...props}>
      {text[state]}
    </div>
  );
};

interface Props {
  id: string;
  name: string;
  code?: string;
  description?: string;
  children: ReactNode;
  environments?: string[];
}

const ExposeDescribe = (props: Props) => {
  const { id, children, name, description, environments } = props;
  const viewports = useWindowWidth();
  const viewportWidth = window.innerWidth;
  const viewportName = Object.entries(viewports)
    .filter(([_, value]) => value)
    .map(([key]) => key)
    .pop();

  const compositeId = slugify(id, { lower: true, strict: true });

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        docs: environments?.includes('docs'),
        exposeOnly: environments?.length === 1 && environments?.includes('expose'),
      })}
    >
      <StyledExposeDescribeWrapper id={compositeId}>
        <StyledSampleInfo>
          <div>
            <SampleTitle title={name} />
            {description && <small>{description}</small>}
          </div>
          <small>
            <BackstopRemove>
              {props.code && (
                <Anchor
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://ink.carta.com/playroom/${createUrl({
                    code: props.code!.replace(/(.*)};?$/gs, '<FunctionWrapper>\n  {$1}}\n</FunctionWrapper>'),
                  })}`}
                >
                  playroom
                </Anchor>
              )}
            </BackstopRemove>{' '}
            {viewportName} <BackstopRemove>({viewportWidth}px)</BackstopRemove>
          </small>
        </StyledSampleInfo>

        <StyledExposeContent className="expose-content">{children}</StyledExposeContent>
      </StyledExposeDescribeWrapper>
    </ThemeProvider>
  );
};

export default ExposeDescribe;
